import * as React from "react";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import styled from "styled-components";
import values from "lodash/values";
import isEmpty from "lodash/isEmpty";
import * as Immutable from "immutable";
import Store from "logic/local-storage/Store";
import { widgetDefinition } from "views/logic/Widgets";
import AggregationWidget from "views/logic/aggregationbuilder/AggregationWidget";
import AggregationWidgetConfig from "views/logic/aggregationbuilder/AggregationWidgetConfig";
import Pivot from "views/logic/aggregationbuilder/Pivot";
import Series from "views/logic/aggregationbuilder/Series";
import WidgetPosition from "views/logic/widgets/WidgetPosition";
import LineVisualizationConfig from "views/logic/aggregationbuilder/visualizations/LineVisualizationConfig";
import AreaVisualizationConfig from "views/logic/aggregationbuilder/visualizations/AreaVisualizationConfig";
import { Alert, Button, Row, Col } from "components/bootstrap";
import Spinner from "components/common/Spinner";
import { TIMESTAMP_FIELD } from "views/Constants";
import useViewsDispatch from "views/stores/useViewsDispatch";
import { updateViewState, executeActiveQuery } from "views/logic/slices/viewSlice";
import { selectActiveViewState, selectActiveQuery } from "views/logic/slices/viewSelectors";
const FIELD_CHARTS_KEY = "pinned-field-charts";
const FIELD_CHARTS_MIGRATED_KEY = "pinned-field-charts-migrated";
const Actions = styled.div`
  margin-top: 10px;
`;
const mapSeriesFunction = (legacySeries) => {
  switch (legacySeries) {
    case "total":
      return "sum";
    case "mean":
      return "avg";
    case "cardinality":
      return "card";
    default:
      return legacySeries;
  }
};
const mapSeries = (legacySeries, field) => {
  const seriesFunction = mapSeriesFunction(legacySeries);
  return `${seriesFunction}(${field})`;
};
const mapVisualization = (legacyVisualization) => {
  switch (legacyVisualization) {
    case "scatterplot":
      return "scatter";
    case "line":
    case "area":
    case "bar":
      return legacyVisualization;
    default:
      throw new Error(`Unsupported visualization ${legacyVisualization}`);
  }
};
const mapTime = (legacyTime) => {
  switch (legacyTime) {
    case "quarter":
      return { unit: "months", value: 3 };
    default:
      return { unit: `${legacyTime}s`, value: 1 };
  }
};
const mapInterpolation = (legacyInterpolation) => {
  switch (legacyInterpolation) {
    case "basis":
    case "bundle":
    case "cardinal":
    case "monotone":
      return "spline";
    case "linear":
    case "step-after":
      return legacyInterpolation;
    default:
      throw new Error(`Unsupported interpolation ${legacyInterpolation}`);
  }
};
const createVisualizationConfig = (legacyInterpolation, visualization) => {
  const interpolation = mapInterpolation(legacyInterpolation);
  switch (visualization) {
    case "line":
      return LineVisualizationConfig.create(interpolation);
    case "area":
      return AreaVisualizationConfig.create(interpolation);
    default:
      return void 0;
  }
};
const _updateExistingWidgetPos = (existingPositions, rowOffset) => {
  const updatedWidgetPos = { ...existingPositions };
  Object.keys(updatedWidgetPos).forEach((widgetId) => {
    const widgetPos = updatedWidgetPos[widgetId];
    updatedWidgetPos[widgetId] = widgetPos.toBuilder().row(widgetPos.row + rowOffset).build();
  });
  return updatedWidgetPos;
};
const _migrateWidgets = (legacyCharts) => async (_dispatch, getState) => {
  const { defaultHeight } = widgetDefinition(AggregationWidget.type);
  const currentView = selectActiveViewState(getState());
  const activeQuery = selectActiveQuery(getState());
  const newWidgetPositions = {};
  const newWidgets = legacyCharts.map((chart, index) => {
    const { field } = chart;
    const series = new Series(mapSeries(chart.valuetype, field));
    const rowPivotConfig = { interval: { type: "timeunit", ...mapTime(chart.interval) } };
    const rowPivot = Pivot.create([TIMESTAMP_FIELD], "time", rowPivotConfig);
    const visualization = mapVisualization(chart.renderer);
    const visualizationConfig = createVisualizationConfig(chart.interpolation, visualization);
    const widgetConfig = AggregationWidgetConfig.builder().visualization(visualization).visualizationConfig(visualizationConfig).series([series]).rowPivots([rowPivot]).build();
    const newWidget = AggregationWidget.builder().newId().timerange(void 0).config(widgetConfig).build();
    const widgetRowPos = defaultHeight * index + 1;
    newWidgetPositions[newWidget.id] = new WidgetPosition(1, widgetRowPos, defaultHeight, Infinity);
    return newWidget;
  });
  const newWidgetsRowOffset = legacyCharts.length * defaultHeight;
  const existingWidgetPos = _updateExistingWidgetPos(currentView.widgetPositions, newWidgetsRowOffset);
  const newViewState = currentView.toBuilder().widgets(
    Immutable.List([
      // @ts-ignore
      ...currentView.widgets,
      ...newWidgets
    ])
  ).widgetPositions({ ...existingWidgetPos, ...newWidgetPositions }).build();
  return { newViewState, currentQueryId: activeQuery };
};
const _onMigrate = async (dispatch, legacyCharts, setMigrating, setMigrationFinished) => {
  setMigrating(true);
  const { newViewState, currentQueryId } = await dispatch(_migrateWidgets(legacyCharts));
  await dispatch(updateViewState(currentQueryId, newViewState));
  await dispatch(executeActiveQuery());
  Store.set(FIELD_CHARTS_MIGRATED_KEY, "finished");
  setMigrating(false);
  setMigrationFinished(true);
};
const _onCancel = (setMigrationFinished) => {
  Store.set(FIELD_CHARTS_MIGRATED_KEY, "discarded");
  setMigrationFinished(true);
};
const MigrateFieldCharts = () => {
  const { t } = useTranslation("search");
  const [migrating, setMigrating] = useState(false);
  const [migrationFinished, setMigrationFinished] = useState(!!Store.get(FIELD_CHARTS_MIGRATED_KEY));
  const legacyCharts = values(Store.get(FIELD_CHARTS_KEY));
  const chartAmount = legacyCharts.length;
  const dispatch = useViewsDispatch();
  const onMigrate = () => _onMigrate(dispatch, legacyCharts, setMigrating, setMigrationFinished);
  if (migrationFinished || isEmpty(legacyCharts)) {
    return null;
  }
  return /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, null, /* @__PURE__ */ React.createElement(Alert, { bsStyle: "warning", title: t("field_charts_migration.title") }, /* @__PURE__ */ React.createElement("br", null), t("field_charts_migration.found_charts", { count: chartAmount }), /* @__PURE__ */ React.createElement("br", null), t("field_charts_migration.save_hint"), /* @__PURE__ */ React.createElement("br", null), /* @__PURE__ */ React.createElement(Actions, null, /* @__PURE__ */ React.createElement(Button, { bsStyle: "primary", onClick: onMigrate, disabled: migrating, className: "save-button-margin" }, t("field_charts_migration.migrate"), " ", migrating && /* @__PURE__ */ React.createElement(Spinner, { text: "" })), /* @__PURE__ */ React.createElement(Button, { onClick: () => _onCancel(setMigrationFinished), disabled: migrating }, t("field_charts_migration.discard"))))));
};
export default MigrateFieldCharts;
